import { includes, isNumber, pick } from 'lodash-es'
import { differenceInMonths, differenceInYears } from 'date-fns'
import type { FamilyUser } from '~/core/types/family'
import { composeFullName } from '~/core/functions'
import { monthDeclension, yearDeclension } from '~/core/constants/wordForms'
import type { FamilyListFromInfoDto, FamilyListFromMasterDto, FamilyListFromProfileDto } from '~/core/api/familyApi'
import { pluralize } from '~/core/utils/pluralize'
import { ADULT_AGE } from '~/core/constants'
import { FamilyProfileOwnership } from '~/core/enums/family'
import type { SentryPlugin } from '~/plugins/captureException'

export const mapFamily = (
  masterData: FamilyListFromMasterDto[],
  profileData: FamilyListFromProfileDto[],
  infoData: FamilyListFromInfoDto[],
  captureException: SentryPlugin,
  isFamilyTransferEnabled: boolean, // TODO[wff_family_profiles_transfer]: удалить параметр
) => {
  if (
    !infoData.length ||
    !masterData.length ||
    !profileData.length ||
    masterData.findIndex(({ isMain }) => isMain) === -1
  ) {
    const errorText = !masterData.length
      ? 'Получен пустой список семейных профилей'
      : 'В списке семейных профилей отсутствует основной профиль'

    const error = new Error(errorText)

    captureException(error)

    throw error
  }

  return masterData
    .map<FamilyUser | null>((item) => {
      // NOTE: sharedInfo приходит для переданных профилей, с profile и info такие профили не приходят
      const { sharedInfo } = item
      const useSharedInfo = isFamilyTransferEnabled && Boolean(sharedInfo)

      const profileItem = useSharedInfo
        ? sharedInfo
        : profileData.find(({ masterFamilyProfileUuid }) => masterFamilyProfileUuid === item.uuid)

      const infoItem = useSharedInfo
        ? undefined
        : infoData.find(({ masterFamilyProfileUuid }) => masterFamilyProfileUuid === item.uuid)

      if (!useSharedInfo && (!profileItem || !infoItem)) {
        if (item.isMain) {
          const error = new Error('Не найден соответствующий элемент списка профилей с profile сервера для основного профиля')

          captureException(error, {
            master_uuid: item.uuid,
          })

          throw error
        }

        captureException(
          new Error('Не найден соответствующий элемент списка профилей с profile сервера'),
          {
            master_uuid: item.uuid,
          },
        )

        return null
      }

      if (!profileItem) {
        return null
      }

      const familyItem: Omit<FamilyUser, 'avatar'> = {
        uuid: item.uuid,
        isMain: item.isMain,
        esiaInfo: item.esiaInfo,
        newPhone: item.newPhone,
        ownership: item.ownership,
        get fullName() {
          return composeFullName(this)
        },
        ...pick(profileItem, ['forename', 'patronymic', 'surname', 'gender']),
        dateBirth: item.dtBirth ?? '',
        get age() {
          if (!this.dateBirth) {
            return null
          }

          return differenceInYears(new Date(), new Date(this.dateBirth))
        },
        get formattedAge() {
          if (!isNumber(this.age) || !this.dateBirth) {
            return null
          }

          if (this.age < 1) {
            const months = differenceInMonths(new Date(), new Date(this.dateBirth))

            return pluralize(months, monthDeclension)
          }

          return pluralize(this.age, yearDeclension)
        },
        get isAdultOrUnknownAge() {
          return this.age === null || this.age >= ADULT_AGE
        },
        get canConfirmEsia() {
          return this.isAdultOrUnknownAge
        },
        get isAdult() {
          return this.age !== null && this.age >= ADULT_AGE
        },
        get isChild() {
          return this.age !== null && this.age < ADULT_AGE
        },
        get isOnTransfer() {
          return this.ownership === FamilyProfileOwnership.TransferRequest
        },
        get isTransferred() {
          return this.ownership === FamilyProfileOwnership.Transferred
        },
        get isAppOnlyInteractive() {
          return this.isTransferred || this.isTransferAcceptRequested
        },
        get isFioValid() {
          return this.forename.length > 1 && this.surname.length > 0
        },
        get isFilled() {
          return Boolean(this.dateBirth) && Boolean(this.gender) && this.isFioValid
        },
        get isTransferAcceptRequested() {
          return includes([FamilyProfileOwnership.AcceptRequest, FamilyProfileOwnership.FromClinic], this.ownership)
        },
        medformsUpdateDate: infoItem?.dtMedformsUpdatedUtc ?? null,
        femaleCalendarId: infoItem?.femaleCalendarId ?? null,
        hasDentalTreatmentPlan: infoItem?.hasDentalTreatmentPlan ?? false,
        hasSignedEdmDocuments: infoItem?.hasSignedEdmDocuments ?? false,
      }

      return {
        ...familyItem,
        avatar: familyItem.gender && item.ageGroup && item.avatarType
          ? `/graphics/family-avatars/${item.ageGroup}-${familyItem.gender}-${item.avatarType}.png`
          : '/graphics/sample-user-avatar.svg',
      }
    })
    .filter(item => Boolean(item)) as FamilyUser[]
}
